import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Alert, Switch, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import axios from 'axios';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ROLES = ['admin', 'radiologist', 'viewer'];

const roleColor = (role) => ({
  admin:        { bgcolor: '#1a0022', color: '#cc88ff', border: '1px solid #550077' },
  radiologist:  { bgcolor: '#001a22', color: '#00bcd4', border: '1px solid #005566' },
  viewer:       { bgcolor: '#111',    color: '#888',    border: '1px solid #333' },
}[role] || { bgcolor: '#111', color: '#888' });

const providerChip = (provider) => ({
  local:    { label: 'LOCAL',    bgcolor: '#001a00', color: '#44cc44', border: '1px solid #226622' },
  azure_ad: { label: 'AZURE AD', bgcolor: '#00001a', color: '#4488ff', border: '1px solid #224488' },
  oidc:     { label: 'OIDC',     bgcolor: '#1a1a00', color: '#cccc44', border: '1px solid #666622' },
}[provider] || { label: provider?.toUpperCase(), bgcolor: '#111', color: '#888' });

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

// ─── Empty form defaults ──────────────────────────────────────────────────────
const blankUser = () => ({
  username: '', email: '', full_name: '', password: '', roles: ['viewer'],
});

// ─── Main component ───────────────────────────────────────────────────────────
export default function Users() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [dialog, setDialog]     = useState(null); // null | 'create' | 'edit' | 'pw'
  const [selected, setSelected] = useState(null); // user being edited
  const [form, setForm]         = useState(blankUser());
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/auth/users');
      setUsers(res.data.users || []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Dialogs ───────────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm(blankUser());
    setFormError('');
    setSelected(null);
    setDialog('create');
  };

  const openEdit = (user) => {
    setForm({
      username:  user.username,
      email:     user.email,
      full_name: user.full_name || '',
      roles:     user.roles || ['viewer'],
      is_active: user.is_active,
      password:  '',
    });
    setFormError('');
    setSelected(user);
    setDialog('edit');
  };

  const openPw = (user) => {
    setForm({ password: '' });
    setFormError('');
    setSelected(user);
    setDialog('pw');
  };

  const closeDialog = () => { setDialog(null); setSelected(null); setFormError(''); };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setFormError('');
    try {
      if (dialog === 'create') {
        await axios.post('/auth/users', {
          username:  form.username.trim().toLowerCase(),
          email:     form.email.trim().toLowerCase(),
          full_name: form.full_name.trim(),
          password:  form.password,
          roles:     form.roles,
        });
      } else if (dialog === 'edit') {
        const body = {
          email:     form.email.trim().toLowerCase(),
          full_name: form.full_name.trim(),
          roles:     form.roles,
          is_active: form.is_active,
        };
        if (form.password) body.password = form.password;
        await axios.put(`/auth/users/${selected.id}`, body);
      } else if (dialog === 'pw') {
        if (!form.password || form.password.length < 8) {
          setFormError('Password must be at least 8 characters');
          return;
        }
        await axios.put(`/auth/users/${selected.id}`, { password: form.password });
      }
      closeDialog();
      load();
    } catch (e) {
      setFormError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (user) => {
    try {
      await axios.put(`/auth/users/${user.id}`, { is_active: !user.is_active });
      load();
    } catch { /* ignore */ }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <CircularProgress size={24} sx={{ color: 'primary.main' }} />
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'primary.main', letterSpacing: 1 }}>
            USER MANAGEMENT
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#555', mt: 0.3 }}>
            {users.length} user{users.length !== 1 ? 's' : ''} · local + federated accounts
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            borderRadius: 0, fontSize: 11, letterSpacing: 1,
            borderColor: '#00bcd4', color: '#00bcd4',
            '&:hover': { bgcolor: 'rgba(0,188,212,0.08)', borderColor: '#00bcd4' },
          }}
        >
          ADD USER
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 0, bgcolor: '#1a0000' }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer sx={{ border: '1px solid #1e1e1e', borderRadius: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#0d0d0d' }}>
              {['Username', 'Full Name', 'Email', 'Roles', 'Provider', 'Status', 'Last Login', ''].map(h => (
                <TableCell key={h} sx={{ color: '#555', fontSize: 10, letterSpacing: 1, py: 1, borderColor: '#1e1e1e' }}>
                  {h.toUpperCase()}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(u => (
              <TableRow
                key={u.id}
                sx={{
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                  opacity: u.is_active ? 1 : 0.45,
                }}
              >
                <TableCell sx={{ color: '#e0e0e0', fontSize: 12, borderColor: '#1a1a1a', fontFamily: 'monospace' }}>
                  {u.username}
                </TableCell>
                <TableCell sx={{ color: '#888', fontSize: 12, borderColor: '#1a1a1a' }}>
                  {u.full_name || '—'}
                </TableCell>
                <TableCell sx={{ color: '#666', fontSize: 11, borderColor: '#1a1a1a', fontFamily: 'monospace' }}>
                  {u.email}
                </TableCell>
                <TableCell sx={{ borderColor: '#1a1a1a' }}>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(u.roles || []).map(r => (
                      <Chip key={r} label={r} size="small" sx={{
                        height: 16, fontSize: 9, letterSpacing: 0.5, borderRadius: 0,
                        ...roleColor(r),
                      }} />
                    ))}
                  </Box>
                </TableCell>
                <TableCell sx={{ borderColor: '#1a1a1a' }}>
                  {(() => { const c = providerChip(u.provider); return (
                    <Chip label={c.label} size="small" sx={{
                      height: 16, fontSize: 9, borderRadius: 0, ...c,
                    }} />
                  ); })()}
                </TableCell>
                <TableCell sx={{ borderColor: '#1a1a1a' }}>
                  <Tooltip title={u.is_active ? 'Active — click to disable' : 'Disabled — click to enable'}>
                    <Chip
                      label={u.is_active ? 'ACTIVE' : 'DISABLED'}
                      size="small"
                      onClick={() => toggleActive(u)}
                      sx={{
                        height: 16, fontSize: 9, borderRadius: 0, cursor: 'pointer',
                        ...(u.is_active
                          ? { bgcolor: '#001a00', color: '#44cc44', border: '1px solid #226622' }
                          : { bgcolor: '#1a0000', color: '#cc4444', border: '1px solid #662222' }
                        ),
                      }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ color: '#555', fontSize: 10, borderColor: '#1a1a1a', fontFamily: 'monospace' }}>
                  {fmtDate(u.last_login)}
                </TableCell>
                <TableCell sx={{ borderColor: '#1a1a1a' }}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit user">
                      <IconButton size="small" onClick={() => openEdit(u)}
                        sx={{ color: '#555', p: 0.4, '&:hover': { color: '#00bcd4' } }}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    {u.provider === 'local' && (
                      <Tooltip title="Reset password">
                        <IconButton size="small" onClick={() => openPw(u)}
                          sx={{ color: '#555', p: 0.4, '&:hover': { color: '#ffeb3b' } }}>
                          <LockResetIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Dialogs ─────────────────────────────────────────────────────────── */}
      <UserDialog
        open={!!dialog}
        mode={dialog}
        form={form}
        setForm={setForm}
        onSave={handleSave}
        onClose={closeDialog}
        saving={saving}
        error={formError}
        selected={selected}
      />
    </Box>
  );
}

// ─── Create / Edit / Password dialog ─────────────────────────────────────────
function UserDialog({ open, mode, form, setForm, onSave, onClose, saving, error, selected }) {
  const isCreate  = mode === 'create';
  const isEdit    = mode === 'edit';
  const isPw      = mode === 'pw';

  const title = isCreate ? 'Add User' : isEdit ? `Edit: ${selected?.username}` : `Reset Password: ${selected?.username}`;

  const f = (key) => form[key] ?? '';
  const set = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  const field = (label, key, opts = {}) => (
    <TextField
      label={label}
      value={f(key)}
      onChange={set(key)}
      fullWidth
      size="small"
      required={opts.required}
      type={opts.type || 'text'}
      autoComplete={opts.autoComplete}
      disabled={opts.disabled}
      InputLabelProps={{ sx: { fontSize: 12, color: '#555' } }}
      InputProps={{ sx: { fontSize: 12, fontFamily: 'monospace', borderRadius: 0 } }}
      sx={{
        '& .MuiOutlinedInput-root': {
          '& fieldset': { borderColor: '#2a2a2a' },
          '&:hover fieldset': { borderColor: '#444' },
          '&.Mui-focused fieldset': { borderColor: 'primary.main' },
        },
      }}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: '#111', border: '1px solid #1e1e1e', borderRadius: 0 } }}
    >
      <DialogTitle sx={{ fontSize: 13, letterSpacing: 1, color: 'primary.main', borderBottom: '1px solid #1e1e1e', fontFamily: 'monospace' }}>
        {title?.toUpperCase()}
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && (
          <Alert severity="error" sx={{ borderRadius: 0, bgcolor: '#1a0000', fontSize: 12 }}>
            {error}
          </Alert>
        )}

        {isPw ? (
          field('New Password', 'password', { required: true, type: 'password', autoComplete: 'new-password' })
        ) : (
          <>
            {field('Username', 'username', { required: true, disabled: isEdit })}
            {field('Email', 'email', { required: true })}
            {field('Full Name', 'full_name')}

            {/* Roles */}
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: 12, color: '#555' }}>Roles</InputLabel>
              <Select
                multiple
                value={form.roles || []}
                onChange={(e) => setForm(p => ({ ...p, roles: e.target.value }))}
                label="Roles"
                renderValue={(sel) => sel.join(', ')}
                sx={{ fontSize: 12, fontFamily: 'monospace', borderRadius: 0,
                  '& fieldset': { borderColor: '#2a2a2a' },
                  '&:hover fieldset': { borderColor: '#444' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                }}
                MenuProps={{ PaperProps: { sx: { bgcolor: '#111', border: '1px solid #2a2a2a', borderRadius: 0 } } }}
              >
                {ROLES.map(r => (
                  <MenuItem key={r} value={r} sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Password (optional on edit) */}
            <TextField
              label={isCreate ? 'Password' : 'New Password (leave blank to keep current)'}
              type="password"
              value={f('password')}
              onChange={set('password')}
              required={isCreate}
              autoComplete="new-password"
              fullWidth
              size="small"
              InputLabelProps={{ sx: { fontSize: 12, color: '#555' } }}
              InputProps={{ sx: { fontSize: 12, fontFamily: 'monospace', borderRadius: 0 } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#2a2a2a' },
                  '&:hover fieldset': { borderColor: '#444' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
              }}
            />

            {/* Active toggle (edit only) */}
            {isEdit && (
              <FormControlLabel
                label={<Typography sx={{ fontSize: 12, color: '#888' }}>Active</Typography>}
                control={
                  <Switch
                    size="small"
                    checked={!!form.is_active}
                    onChange={(e) => setForm(p => ({ ...p, is_active: e.target.checked }))}
                    sx={{ '& .MuiSwitch-thumb': { bgcolor: form.is_active ? '#00bcd4' : '#555' } }}
                  />
                }
              />
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #1e1e1e', px: 2.5, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} size="small"
          sx={{ fontSize: 11, color: '#555', borderRadius: 0, letterSpacing: 1 }}>
          CANCEL
        </Button>
        <Button
          onClick={onSave}
          disabled={saving}
          variant="outlined"
          size="small"
          sx={{
            fontSize: 11, borderRadius: 0, letterSpacing: 1,
            borderColor: '#00bcd4', color: '#00bcd4',
            '&:hover': { bgcolor: 'rgba(0,188,212,0.08)' },
          }}
        >
          {saving ? <CircularProgress size={14} sx={{ color: 'primary.main' }} /> : 'SAVE'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
